/**
 * dateRangeHelper.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared helper for building Supabase date range filters in Europe/Madrid
 * local time.
 *
 * PATTERN used for Supabase:
 *   fecha >= startOfDayLocal(fechaDesde)              (inclusive)
 *   fecha <  startOfDayLocal(fechaHasta + 1 day)      (exclusive — includes full last day)
 *
 * This avoids:
 *  - The classic bug of comparing against 23:59:59 which misses records at 23:59:59.xxx
 *  - UTC-shift errors from new Date("YYYY-MM-DD") which is parsed as UTC midnight
 *
 * TIMEZONE: Europe/Madrid (UTC+1 winter, UTC+2 summer)
 */

const TIMEZONE = 'Europe/Madrid';

/**
 * Get the UTC offset in milliseconds for Europe/Madrid at a given reference Date.
 *
 * Strategy: format the date in Madrid timezone, parse it back as if it were UTC,
 * then compute the difference. This gives us how many ms Madrid is AHEAD of UTC.
 *
 * Example in summer (UTC+2):
 *   refDate = 2026-08-01T12:00:00Z  → UTC ms = X
 *   madridStr = "08/01/2026, 14:00:00"  → parsed as UTC = X + 7200000
 *   madridAheadMs = (X + 7200000) - X = +7200000  (Madrid is 2h ahead)
 *
 * To convert Madrid local midnight to UTC: subtract madridAheadMs
 *   UTC midnight = Madrid midnight - 7200000
 */
const getMadridAheadMs = (refDate) => {
  // Format the reference date in Madrid timezone
  const madridStr = refDate?.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  // Parse the Madrid local time as if it were UTC
  // en-US format: "MM/DD/YYYY, HH:MM:SS"
  const madridAsUtcMs = new Date(madridStr.replace(',', ''))?.getTime();
  // How many ms Madrid is ahead of UTC
  return madridAsUtcMs - refDate?.getTime();
};

/**
 * Given a YYYY-MM-DD string, return the UTC ISO string for 00:00:00 of that
 * day in Europe/Madrid local time.
 *
 * Example: "2026-08-01" in Madrid (UTC+2 in summer)
 *   → Madrid midnight = 2026-08-01 00:00:00 local
 *   → UTC equivalent  = 2026-07-31T22:00:00.000Z
 */
export const localDateToUTCStart = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr?.split('-')?.map(Number);
  // Use noon as reference to get the offset (avoids DST edge cases at midnight)
  const noonRef = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const aheadMs = getMadridAheadMs(noonRef);
  // Madrid midnight as a UTC timestamp: midnight local = midnight UTC - aheadMs
  // i.e. if Madrid is UTC+2, midnight Madrid = 22:00 UTC previous day
  const midnightUTC = Date.UTC(year, month - 1, day, 0, 0, 0, 0) - aheadMs;
  return new Date(midnightUTC)?.toISOString();
};

/**
 * Given a YYYY-MM-DD string, return the UTC ISO string for 00:00:00 of the
 * NEXT day in Europe/Madrid local time.
 *
 * Using "start of next day" (exclusive upper bound) instead of "23:59:59"
 * ensures records at any time during the last day are included (23:59:59.999).
 *
 * Example: "2026-08-08" → start of "2026-08-09" in Madrid UTC
 *   → 2026-08-08T22:00:00.000Z  (UTC+2 summer)
 */
export const localDateToUTCEndExclusive = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr?.split('-')?.map(Number);
  // Next day noon as reference for offset
  const nextDayNoonRef = new Date(Date.UTC(year, month - 1, day + 1, 12, 0, 0));
  const aheadMs = getMadridAheadMs(nextDayNoonRef);
  // Next day midnight in Madrid = next day midnight UTC - aheadMs
  const nextDayMidnightUTC = Date.UTC(year, month - 1, day + 1, 0, 0, 0, 0) - aheadMs;
  return new Date(nextDayMidnightUTC)?.toISOString();
};

/**
 * Apply a custom date range to a Supabase query.
 *
 * Rules:
 *  - Only dateFrom  → column >= start of dateFrom
 *  - Only dateTo    → column <  start of day after dateTo
 *  - Both           → both bounds applied
 *
 * @param {object} query  - Supabase query builder
 * @param {string} dateFrom - YYYY-MM-DD or empty string
 * @param {string} dateTo   - YYYY-MM-DD or empty string
 * @param {string} column   - column name (default: 'created_at')
 * @returns {object} query with date filters applied
 */
export const applyCustomDateRange = (query, dateFrom, dateTo, column = 'created_at') => {
  if (dateFrom && dateFrom !== '') {
    const fromUTC = localDateToUTCStart(dateFrom);
    if (fromUTC) query = query?.gte(column, fromUTC);
  }
  if (dateTo && dateTo !== '') {
    const toUTC = localDateToUTCEndExclusive(dateTo);
    if (toUTC) query = query?.lt(column, toUTC);
  }
  return query;
};

/**
 * Calculate a quick period date range in Europe/Madrid timezone.
 * Returns { fromUTC: string, toUTCExclusive: string } — ISO strings for Supabase.
 *
 * Supported periods:
 *   today | yesterday | week | thisWeek | lastWeek | month | thisMonth |
 *   lastMonth | thisQuarter | year | thisYear
 */
export const getQuickPeriodUTCRange = (period) => {
  const now = new Date();
  // Get current date parts in Madrid timezone
  const madridStr = now?.toLocaleString('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-US format: "MM/DD/YYYY"
  const [mStr, dStr, yStr] = madridStr?.split('/');
  const year = parseInt(yStr);
  const month = parseInt(mStr) - 1; // 0-indexed
  const day = parseInt(dStr);

  const pad = (n) => String(n)?.padStart(2, '0');
  const toDateStr = (y, mo, d) => {
    // Use a Date to handle month/day overflow correctly
    const dt = new Date(y, mo, d);
    return `${dt?.getFullYear()}-${pad(dt?.getMonth() + 1)}-${pad(dt?.getDate())}`;
  };
  const startOf = (y, mo, d) => localDateToUTCStart(toDateStr(y, mo, d));
  const endExclOf = (y, mo, d) => localDateToUTCEndExclusive(toDateStr(y, mo, d));

  switch (period) {
    case 'today':
      return { fromUTC: startOf(year, month, day), toUTCExclusive: endExclOf(year, month, day) };

    case 'yesterday':
      return { fromUTC: startOf(year, month, day - 1), toUTCExclusive: endExclOf(year, month, day - 1) };

    case 'week': case'thisWeek': {
      const jsDay = new Date(year, month, day)?.getDay(); // 0=Sun
      const daysToMonday = jsDay === 0 ? -6 : 1 - jsDay;
      const monDay = day + daysToMonday;
      const sunDay = monDay + 6;
      return { fromUTC: startOf(year, month, monDay), toUTCExclusive: endExclOf(year, month, sunDay) };
    }

    case 'lastWeek': {
      const jsDay = new Date(year, month, day)?.getDay();
      const daysToMonday = jsDay === 0 ? -6 : 1 - jsDay;
      const thisMonDay = day + daysToMonday;
      const lastMonDay = thisMonDay - 7;
      const lastSunDay = thisMonDay - 1;
      return { fromUTC: startOf(year, month, lastMonDay), toUTCExclusive: endExclOf(year, month, lastSunDay) };
    }

    case 'month': case'thisMonth': {
      const lastDay = new Date(year, month + 1, 0)?.getDate();
      return { fromUTC: startOf(year, month, 1), toUTCExclusive: endExclOf(year, month, lastDay) };
    }

    case 'lastMonth': {
      const lastDayPrev = new Date(year, month, 0)?.getDate();
      return { fromUTC: startOf(year, month - 1, 1), toUTCExclusive: endExclOf(year, month - 1, lastDayPrev) };
    }

    case 'thisQuarter': {
      const q = Math.floor(month / 3);
      const qStartMonth = q * 3;
      const qEndMonth = qStartMonth + 2;
      const lastDayQ = new Date(year, qEndMonth + 1, 0)?.getDate();
      return { fromUTC: startOf(year, qStartMonth, 1), toUTCExclusive: endExclOf(year, qEndMonth, lastDayQ) };
    }

    case 'year': case'thisYear':
      return { fromUTC: startOf(year, 0, 1), toUTCExclusive: endExclOf(year, 11, 31) };

    default:
      return null;
  }
};

/**
 * Apply a quick period filter to a Supabase query.
 *
 * @param {object} query
 * @param {string} period
 * @param {string} column
 * @returns {object} query
 */
export const applyQuickPeriodFilter = (query, period, column = 'created_at') => {
  const range = getQuickPeriodUTCRange(period);
  if (!range) return query;
  return query?.gte(column, range?.fromUTC)?.lt(column, range?.toUTCExclusive);
};

/**
 * getDateRange — Unified entry point for both quick and custom date ranges.
 *
 * Returns { fromUTC: string|null, toUTCExclusive: string|null }
 *
 * Supports:
 *   period = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | *'lastMonth' | 'thisQuarter' | 'thisYear' | 'custom'
 *
 * For 'custom': uses dateFrom and/or dateTo (YYYY-MM-DD strings).
 *   - Only dateFrom  → fromUTC set, toUTCExclusive null
 *   - Only dateTo    → fromUTC null, toUTCExclusive set
 *   - Both           → both set
 *
 * Activating custom automatically when dateFrom or dateTo is provided:
 *   If period !== 'custom' but dateFrom or dateTo is non-empty, treats as custom.
 *
 * @param {string} period
 * @param {string} [dateFrom] - YYYY-MM-DD
 * @param {string} [dateTo]   - YYYY-MM-DD
 * @returns {{ fromUTC: string|null, toUTCExclusive: string|null }}
 */
export const getDateRange = (period, dateFrom = '', dateTo = '') => {
  const hasFrom = dateFrom && dateFrom !== '';
  const hasTo = dateTo && dateTo !== '';

  // If manual dates are provided, always use custom logic regardless of period
  if (period === 'custom' || hasFrom || hasTo) {
    return {
      fromUTC: hasFrom ? localDateToUTCStart(dateFrom) : null,
      toUTCExclusive: hasTo ? localDateToUTCEndExclusive(dateTo) : null,
    };
  }

  // Quick period
  if (period && period !== '') {
    const range = getQuickPeriodUTCRange(period);
    if (range) return range;
  }

  return { fromUTC: null, toUTCExclusive: null };
};
